import buildsService from '../services/buildsService';
import tasksService from '../services/tasksService';
import dataModel from '../models/DataModel';
import OfflineQueue from './queue/OfflineQueue';
import DAO from './DAO.js';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import Metadata from '../helpers/metadata/Metadata';

//Record descriptor
export const build_root = {
  build: {
    builds: {},
    tasks: { project: {}, user_task_assignments: { user: { additionalFields: ['profile_image'] } } },
    user_build_assignments: {
      user: { },
    },
    build_owners: { companies: {} },
  },
};

export const build_card = {
  build: {
    builds: {},
    user_build_assignments: {
      user: { },
    },
    build_owners: { companies: { additionalFields: ['logo'] } },
    location: {},
  },
};

export const attributes_build_root = {
  build: {},
};

//ADD METADATA
export default class BuildsDAO extends DAO {

  /*************************************************************
   *
   *  Initialization
   *
   *************************************************************/

  constructor(store) {
    super(store, 'build');
    this.OfflineQueue = OfflineQueue;

    this.unsetBuild = this.unsetBuild.bind(this);
    this.clearBuilds = this.clearBuilds.bind(this);
    this.updateBuild = this.updateBuild.bind(this);
    this.mapToReturn = this.mapToReturn.bind(this);
  }

  mapToReturn(build, options) {
    let denormalizedBuild = super.mapToReturn(build, options);
    return denormalizedBuild;
  }

  unsetBuild(buildId) {
    StoreItemProcessing.deleteItemInStore(this.itemType, buildId);
  }

  clearBuilds() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  async getBuild(buildId, options, prefetchCallback, projectId, projectTemplateId) {
    if (buildId == 'root_build') {
      if (projectId) {
        let denormalizedProject = await dataModel.projectsDAO.getProject(projectId, {
          force: options.force,
          recordDescriptor: {
            project: {
              builds: {
                tasks: {
                  project: {},
                  user_task_assignments: { user: { additionalFields: ['profile_image'] } },
                },
              },
            },
          },
        }, null);
        return Promise.resolve(denormalizedProject.root_build);
      }
      else if (projectTemplateId) {
        let denormalizedProjectTemplate = await dataModel.projectTemplatesDAO.getProjectTemplate(projectTemplateId, {
          recordDescriptor: {
            project_template: {
              builds: {
                tasks: {
                  project: {},
                },
              },
            },
          },
        }, false, null);
        return Promise.resolve(denormalizedProjectTemplate.root_build);
      }
    }
    else {
      return this._getItem(buildId, buildsService.getBuild.bind(this, buildId, options ? options : null), options, this.mapToReturn);
    }
  }

  async getBuildTasks(buildId, taskOptions, buildOptions) {
    const build = this.findItem(buildId);
    if (build && build.tasks) {
      if (this.shouldRefresh(build, { recordDescriptor: { build: { tasks: {} } } })) {
        return tasksService.getTasksByIds(build.tasks, taskOptions).then(tasks => {
          const ids = StoreItemProcessing.normalizeAndSyncCollectionToStore('task', tasks);
          build.tasks = ids;
          StoreItemProcessing.setItemInStore('build', StoreItemProcessing.normalizeItem(build));
          let foundProject = this.findItem(build.id);
          return this.mapToReturn(foundProject, buildOptions);
        });
      }
      else {
        return this.mapToReturn(build, buildOptions);
      }
    }
    else {
      return this.getBuild(buildId, buildOptions);
    }
  }

  /* BUILD CRUD FUNCTIONS */

  /**
  * @param  {object <build>} build - denormalized build to create
  * @param  {object <object>} options.recordDescriptor - used to format returned build
  * 
  * @returns {object<Promise<build>>} build - denormalized build
  */

  handleOfflineResponse(denormalizedBuild, activity, eventMetadatum, options) {
    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }
    this.markDirty(denormalizedBuild, true);
    this.OfflineQueue.addQueueItem(this.itemType, activity, { build: denormalizedBuild, eventMetadatum, options });

    return denormalizedBuild;
  }

  createBuild(build, eventMetadatum, options) {
    return buildsService.createBuild(build, eventMetadatum, options).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Creating a build', response.error, false);
        this.throwItemError('Error creating a build', response);
      }
      else {
        const createdBuild = this.normalizeAndSyncToStore(response);
        const newBuild = this.mapToReturn(this.findItem(createdBuild.id), options);
        return newBuild;
      }
    })
    .catch(error => {
      throw error;
    });
  }

    /**
  * @param  {object <build>} build - denormalized build to update
  * @param  {object <object>} options.recordDescriptor - used to format returned build
  * 
  * @returns {object<Promise<build>>} build - denormalized build
  */

  updateBuild(build, eventMetadatum, options) {
    return new Promise((resolve) => {
      this.markPending(build);

      this.normalizeItem(build, { store: true });
      dataModel.actionCableConnector.internalUpdate({ type: 'updateBuild', build_id: build.id }, build.id);
      
      if (dataModel.isOffline) {
        return resolve(this.handleOfflineResponse(build, 'update', eventMetadatum, options));
      }
      else {
        return resolve(buildsService.updateBuild(build, eventMetadatum, options).then(response => {
          if (response.error && !response.offline) {
            dataModel.displayHTTPError('Updating a build', response.error, false);
            this.markError(build);
            this.throwItemError();
          }
          else if (response.offline) {
            return this.handleOfflineResponse(build, 'update', eventMetadatum, options);
          }
          else {
            this.markDirty(build, false);
            this.normalizeAndSyncToStore(response);
            return this.mapToReturn(this.findItem(build.id), options);
          }
        })
          .catch(error => {
            throw error;
          }));
      }
    });
  }

  async copyBuild(build, build_changedName, selectedTasks) {
    return buildsService.copyBuild(build, build_changedName, selectedTasks).then(response => {
      return response;
    })
    .catch((error) => {
      this.throwItemError(error);
    });
  }

  /**
  * @param  {number} buildId - build record identifier
  * 
  * @returns {object<Promise<string>>} result - "offline", "error", "success"
  */
  deleteBuild(buildId, eventMetadatum) {
    let build = this.findItem(buildId);
    if (build) {
      this.unsetBuild(buildId);
    }

    if (!eventMetadatum) {
      eventMetadatum = Metadata.generateMetadatum();
    }

    if (dataModel.isOffline) {
      this.OfflineQueue.addQueueItem(this.itemType, 'delete', { buildId, eventMetadatum });
      this.throwItemError();
    } 
    else {    
      return buildsService.deleteBuild(buildId, eventMetadatum).then(response => {
        if (response.error && !response.offline) {
          dataModel.displayHTTPError('Deleting a build', response.error, false);
          if (build) {
            StoreItemProcessing.setItemInStore(this.itemType, StoreItemProcessing.normalizeItem(build));
          }
          this.throwItemError();
        }
        else if (response.offline) {
          this.OfflineQueue.addQueueItem(this.itemType, 'delete', { buildId, eventMetadatum });
          this.throwItemError();
        }
        else {
          dataModel.displayInfoErrorMessage('The build was successfully deleted.', 4000, 1);
          return this.mapToReturn(build);
        } 
      });
    }
  }

  reorderBuilds(reorderList, project, projectTemplate) {
    //TODO: What status should be marked?
    return new Promise(async resolve => {
      if (reorderList && reorderList.length > 0) {
        const ancestors = reorderList[0].ancestry.split('/');
        if (ancestors.length > 0) {
          const build = this.mapToReturn(this.findItem(+ancestors[ancestors.length - 1]), { recordDescriptor: { build: { builds: {} } } });
          if (build) {
            reorderList.forEach(builds => {
              build.builds.forEach(builds2 => {
                if (builds2.id == builds.id) {
                  builds2.order = builds.order;
                }
              });
            });
            build.builds.sort((t1, t2) => {
              return t1.order < t2.order ? -1 : t1.order > t2.order ? 1 : 0;
            });
            dataModel.buildsDAO.normalizeItem(build, { store: true });
            dataModel.actionCableConnector.internalUpdate({ type: 'updateBuild', build_id: build.id }, build.id);
            if (build.buildable_type == 'Project') {
              dataModel.actionCableConnector.internalUpdate({ type: 'updateProject', project_id: project.id }, project.id);
            }
            else {
              dataModel.actionCableConnector.internalUpdate({ type: 'updateProjectTemplate', project__template_id: projectTemplate.id }, projectTemplate.id);
            }
          }
        }

        let reorderPairs = reorderList.map(build => { return { id: build.id, order: build.order }; });
        // dataModel.inProgress = 30000\
        if (project) {
          return buildsService.reorderBuilds(reorderPairs, project, null).then(response => {
            dataModel.projectsDAO.normalizeAndSyncToStore(response);
            dataModel.actionCableConnector.internalUpdate({ type: 'updateProject', project_id: project.id }, project.id);
            // dataModel.inProgress = 0
            return reorderPairs;
          });
        }
        else if (projectTemplate) {
          return buildsService.reorderBuilds(reorderPairs, null, projectTemplate).then(response => {
            dataModel.projectsDAO.normalizeAndSyncToStore(response);
            dataModel.actionCableConnector.internalUpdate({ type: 'updateProjectTemplate', project_template_id: projectTemplate.id }, projectTemplate.id);
            // dataModel.inProgress = 0
            return reorderPairs;
          });
        }
      }
      else {
        resolve(null);
      }
    });
  }

  setBreadCrumbs(breadCrumbs) {
    //TODO: What should we do about breadcrumbs?
    this.store.dispatch({
      type: 'SET_BREADCRUMBS',
      breadCrumbs: breadCrumbs,
    });
  }

  addUserAssignment(build, project, assignment, options) {
    const userAlreadyAdded = build.user_build_assignments.find(assignedUser => assignedUser.user_id == assignment.user.id);
    if (!userAlreadyAdded) {
      build.user_build_assignments.push(assignment);
    }
    build.build_owners = !build.user_build_assignments ? [] : build.user_build_assignments.filter(uba => {return uba.assignment_type == 'build_owner';}).map(uba => { return uba.user; });
    this.normalizeItem(build, { store: true });
    dataModel.actionCableConnector.internalUpdate({ type: 'updateBuild', task_id: build.id }, build.id);

    if (dataModel.isOffline) {
      this.markDirty(build, true);
      dataModel.offlineQueue.addQueueItem('build', 'add_assignment', { build, project, assignment });
      return Promise.resolve(build);
    }
    else {
      const serviceCall = assignment.assignment_type == 'notify' ? buildsService.createNotify : buildsService.createBuildOwner;
      return serviceCall(build, project, assignment).then(response => {
        if (response.offline) {
          this.markDirty(build, true);
          dataModel.offlineQueue.addQueueItem('build', 'add_assignment', { build, project, assignment });
          return build;
        }
        else if (response.error) {
          dataModel.displayHTTPError('Adding build assignment', response.error, false);
        }
        else {
          this.markDirty(build, false);
          this.normalizeAndSyncToStore(response);
          return this.mapToReturn(this.findItem(build.id), options);
        }
      });
    }
  }

  removeUserAssignment(build, assignment, removeAllBuildOwners, options) {
    const userIndex = build.user_build_assignments.findIndex(assignedUser => assignedUser.user_id == assignment.user.id);
    if (userIndex >= 0) {
      build.user_build_assignments.splice(userIndex, 1);
      this.markNeedsSync(build);
      
      if (removeAllBuildOwners) {
        build.build_owner_ids = [];
        build.build_owners = [];
      }
    }

    this.normalizeItem(build, { store: true });
    dataModel.actionCableConnector.internalUpdate({ type: 'updateBuild', task_id: build.id }, build.id);

    if (dataModel.isOffline) {
      this.markDirty(build, true);
      dataModel.offlineQueue.addQueueItem('build', 'remove_assignment', { build, assignment });
      return Promise.resolve(build);
    }
    else {
      const serviceCall = assignment.assignment_type == 'notify' ? buildsService.deleteNotify : buildsService.deleteBuildOwner;
      return serviceCall(build, assignment).then(response => {
        if (response) {
          if (response.offline) {
            this.markDirty(build, true);
            dataModel.offlineQueue.addQueueItem('build', 'remove_assignment', { build, assignment });
            return build;
          }
          else if (response.error) {
            dataModel.displayHTTPError('Removing build assignment', response.error, false);
          }
          else {
            dataModel.userBuildAssignmentsDAO.unsetUserBuildAssignments(assignment.id);
            this.markDirty(build, false);
            this.normalizeAndSyncToStore(response);
            return this.mapToReturn(this.findItem(build.id), options);
          }
        }
      });
    }

  }

  // format of bulkAssignments is { user_id: is of user to assign to, assignment_type: one of "build_owner" or "notify", builds: [list of build ids] }
  bulkAssign(bulkAssignments) {
    const userId = bulkAssignments.user_id;
    const buildIds = bulkAssignments.builds;

    let userErrors = [];

    const user = dataModel.usersDAO.mapToReturn(dataModel.usersDAO.findItem(userId), { recordDescriptor: { user: { role: {}, profile_image: {} } } });
    if (bulkAssignments.assignment_type == 'build_owner' && user.role.name != 'admin' && user.role.name != 'editor') {
      userErrors.push(user);
      return new Promise(resolve => {
        return resolve(userErrors);
      });
    }
    else {

      bulkAssignments.builds = buildIds.filter(buildId => {
        const build = this.mapToReturn(this.findItem(buildId), { recordDescriptor: { build: { user_build_assignments: {} } } });
        if (build && build.user_build_assignments && build.user_build_assignments.length) {
          const errors = build.user_build_assignments.filter(uba => {
            if (uba.user_id == userId) {
              if (uba.assignment_type == bulkAssignments.assignment_type) {
                return true;
              }
              else if (uba.assignment_type != 'notify' && bulkAssignments.assignment_type != 'notify') {
                if (!userErrors.find(ue => { return ue.id == userId; })) {
                  userErrors.push(user);
                }
                return true;
              }
              else {
                return false;
              }
            }
            else {
              return false;
            }
          });
          return errors.length == 0;
        }
        return true;
      });

      if (bulkAssignments.builds.length == 0) {
        return new Promise(resolve => {
          return resolve(userErrors);
        });
      }
      else {
        return buildsService.bulkAssign(bulkAssignments).then(response => {
          if (response.error) {
            dataModel.displayHTTPError('Doing a bulk assign', response.error, false);
          }
          return userErrors;
        })
          .catch((error) => {
            this.throwItemError();
          });
      }
    }
  }

  /* Util Functions */
  completeOfflineUpdate(queueItem) {
    const eventMetadatum = queueItem.itemInfo.eventMetadatum;
    const options = queueItem.itemInfo.options;
    switch (queueItem.operationKey) {
    case 'create':
      return this.createBuild(queueItem.itemInfo.build, eventMetadatum, options);
    case 'update':
      return this.updateBuild(queueItem.itemInfo.build, eventMetadatum, options);
    case 'delete':
      return this.deleteBuild(queueItem.itemInfo.buildId, eventMetadatum, options);
    case 'add_assignment':
      return this.addUserAssignment(queueItem.itemInfo.build, queueItem.itemInfo.project, queueItem.itemInfo.assignment, options);
    case 'remove_assignment':
      return this.removeUserAssignment(queueItem.itemInfo.build, queueItem.itemInfo.assignment, null, options);
    default:
      break;
    }
  }

  handleActionCableAndGetRequest(acData, mark=true) {
    const build = StoreItemProcessing.findItem(this.itemType, acData.build_id);
    if (mark) {
      if (build) {
        // ensure all delete updates are using attribute syncs
        if (acData.is_attribute_update) {
          this.markNeedsAttributeSync(build);
        }
        else {
          this.markNeedsSync(build);
        }
      }
    }

    let options = {};
    if (!acData.is_attribute_update) {
      options.recordDescriptor = build_root;
    }
    switch (acData.type) {
    case 'newBuild':
    case 'updateBuild':
      return this.getBuild.bind(this);
    case 'deleteBuild':
      const deletedBuild = StoreItemProcessing.deleteItemInStore(this.itemType, acData.build_id);
      return Promise.resolve(deletedBuild);
    default:
    }
  }

  clearState(options) {
    this.clearMyState(this.stateTree.new_builds, options.toIgnore.builds, this.unsetBuild, this.clearBuilds, options);
  }
}



// WEBPACK FOOTER //
// ./src/dao/BuildsDAO.js